import { useSpotUser } from "@/utils/spotUser";
import { Group, GroupOrder, GroupWithUsers, UsersService } from "@/generated";
import { ChevronDown, Plus } from "lucide-react";
import { DATA_KEYS, dataWrapper, useRemoteData } from "@/utils/useRemoteData";
import { useCallback, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { getInitials } from "@/utils/generic";
import { GroupModal } from "@/components/Modals/GroupModal";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { SearchBar } from "@/components/SearchBar";
import { SpotToast } from "@/components/SpotToast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/shadcn/sidebar";
import { Skeleton } from "@/components/shadcn/skeleton";
import { useDisclosure } from "@/utils/useDisclosure";
import { usePermissions } from "@/hooks/usePermissions";
import { ORG_PERMISSIONS } from "@/constants/permissions";
import { NoxusEntityIcon } from "@/components/UI/NoxusEntityIcon";
import { useBlockSidenavCollapse } from "./sidenavCollapseBlock";

const WorkspaceButton = ({
  group,
  isSelected,
  isMember,
  isOrgAdmin,
  onClick,
  isFirstItem = false,
  onUpArrow,
}: {
  group: Group;
  isSelected?: boolean;
  isMember?: boolean;
  isOrgAdmin?: boolean;
  onClick: () => void;
  isFirstItem?: boolean;
  onUpArrow?: () => void;
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isFirstItem && e.key === "ArrowUp") {
      e.preventDefault();
      onUpArrow?.();
    }
  };

  // Org admins can switch into any workspace even without membership.
  const canSwitch = isMember !== false || isOrgAdmin;

  return (
    <DropdownMenuItem
      onClick={canSwitch ? onClick : undefined}
      onKeyDown={handleKeyDown}
      disabled={!canSwitch}
      className="gap-2 p-2"
    >
      <NoxusEntityIcon
        value={group.name}
        color={group.settings?.color as string | undefined}
        isSmall
      />
      <span className="truncate">{group.name}</span>
      {isMember === false && (
        <span className="ml-auto shrink-0 text-[12px] font-medium text-ring">
          Not joined
        </span>
      )}
    </DropdownMenuItem>
  );
};

export const WorkspaceSwitcher = () => {
  const setIsCollapseBlocked = useBlockSidenavCollapse("workspace-switcher");
  const router = useRouter();
  const { hasAllPermissions } = usePermissions();
  const { spotUser, spotGroup, setActiveGroupId } = useSpotUser();
  const { isMobile } = useSidebar();
  const [searchValue, setSearchValue] = useState<string>("");
  const [isOpenGroupModal, setIsOpenGroupModal] = useState(false);
  const { onToggle } = useDisclosure();
  const observer = useRef<IntersectionObserver>(undefined);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const trimmedSearch = searchValue.trim();
  const hasAllGroupsPermission = hasAllPermissions(
    [ORG_PERMISSIONS.WORKSPACE.READ],
    "tenant"
  );
  const isOrgAdmin = hasAllPermissions([ORG_PERMISSIONS.ORG.ADMIN], "tenant");

  const {
    data: groupsData,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      DATA_KEYS.GROUPS,
      hasAllGroupsPermission ? DATA_KEYS.ALL_GROUPS : DATA_KEYS.MY_GROUPS,
      trimmedSearch,
    ],
    queryFn: ({ pageParam }) =>
      dataWrapper(
        hasAllGroupsPermission
          ? UsersService.getAllGroups
          : UsersService.getMyGroups
      )({
        query: {
          page: pageParam as number,
          size: 30,
          name: trimmedSearch || null,
          order_by: GroupOrder.GROUP_NAME,
          sort: "asc",
        },
      }),
    refetchOnWindowFocus: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined;
    },
  });

  const updateMeMutation = useMutation({
    mutationFn: UsersService.updateMe,
    onError: () => {
      SpotToast.error("There was an error. Please try agin later.");
    },
  });

  const groups = useMemo(() => {
    const allGroups =
      groupsData?.pages.reduce(
        (acc: GroupWithUsers[], page: any) => [...acc, ...page.items],
        []
      ) || [];

    // Sort: workspaces the user belongs to first
    return allGroups.sort((a, b) => {
      const aIsMember = a.users.some(
        (u: { user: { id: string } }) => u.user.id === spotUser?.id
      );
      const bIsMember = b.users.some(
        (u: { user: { id: string } }) => u.user.id === spotUser?.id
      );
      if (aIsMember && !bIsMember) return -1;
      if (!aIsMember && bIsMember) return 1;
      return 0;
    });
  }, [groupsData, spotUser?.id]);

  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting && hasNextPage) {
          void fetchNextPage();
        }
      });

      if (node) observer.current.observe(node);
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading]
  );

  const handleGroupClick = (group: GroupWithUsers) => {
    if (spotGroup && spotUser) {
      if (group.group.id !== spotGroup.id) {
        // Update sessionStorage immediately for per-tab isolation
        setActiveGroupId(group.group.id);
        // Persist to backend so new tabs/sessions inherit the selection
        updateMeMutation.mutate({
          body: {
            settings: { ...spotUser?.settings, defaultGroup: group.group.id },
          },
        });
        // Redirect to home so the router resolves the correct landing page
        router.push("/");
      }
      onToggle();
    }
  };

  // Reset search value when dropdown opens
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    setIsCollapseBlocked(open);
    if (open) {
      setSearchValue("");
    }
  };

  if (!spotGroup) {
    return null;
  }

  const canCreateWorkspace = hasAllPermissions(
    [ORG_PERMISSIONS.WORKSPACE.WRITE],
    "tenant"
  );

  return (
    <>
      <SidebarMenu className="w-fit min-w-0">
        <SidebarMenuItem className="min-w-0">
          <DropdownMenu
            open={isOpen}
            onOpenChange={handleOpenChange}
            modal={false}
          >
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                data-testid="workspace-switcher"
                disabled={updateMeMutation.status === "pending"}
                className="w-fit max-w-full h-8 p-1 group-data-[collapsible=icon]:p-1!"
              >
                <NoxusEntityIcon
                  value={spotGroup.name}
                  color={spotGroup.settings?.color as string | undefined}
                  isSmall
                />
                <span className="truncate text-sm font-medium flex-1 text-left min-w-0">
                  {spotGroup.name}
                </span>
                <ChevronDown className="size-3.5 -ml-1 shrink-0" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 max-w-72 rounded-lg"
              align="start"
              side="bottom"
              sideOffset={4}
            >
              <div className="w-full px-2 pt-3 pb-2">
                <DropdownMenuLabel className="text-muted-foreground text-xs p-0 mb-4">
                  Workspaces
                </DropdownMenuLabel>
                <div
                  onKeyDown={(e) => {
                    if (
                      (e.key === "ArrowDown" || e.key === "Tab") &&
                      searchInputRef.current === document.activeElement
                    ) {
                      e.preventDefault();
                      const firstWorkspaceButton = document.querySelector(
                        '[role="menuitem"]'
                      ) as HTMLElement;
                      if (firstWorkspaceButton) {
                        firstWorkspaceButton.focus();
                      }
                    } else {
                      if (searchInputRef.current === document.activeElement) {
                        e.stopPropagation();
                      }
                    }
                  }}
                >
                  <SearchBar
                    value={searchValue}
                    setValue={setSearchValue}
                    isDebounced
                    placeholder="Search workspaces"
                    autoFocus
                    searchInputRef={searchInputRef}
                  />
                </div>
              </div>

              <div className="w-full max-h-90 overflow-y-auto thin-scrollbars">
                {(groups && groups.length > 0) || isLoading ? (
                  <div>
                    {!isLoading &&
                      groups.map((group, index) => (
                        <div key={`group-${index}`}>
                          <WorkspaceButton
                            group={group.group}
                            isSelected={group.group.id === spotGroup.id}
                            isMember={group.users.some(
                              (u: { user: { id: string } }) =>
                                u.user.id === spotUser?.id
                            )}
                            isOrgAdmin={isOrgAdmin}
                            onClick={() => handleGroupClick(group)}
                            isFirstItem={index === 0}
                            onUpArrow={() => searchInputRef.current?.focus()}
                          />
                        </div>
                      ))}

                    <div ref={lastElementRef} />

                    {(isLoading || isFetchingNextPage) && (
                      <Skeleton className="h-9 w-full rounded-sm" />
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm px-3 pt-2 pb-4">
                    No workspaces to display
                  </p>
                )}
              </div>

              {canCreateWorkspace && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="gap-2 p-2"
                    onClick={() => setIsOpenGroupModal(true)}
                  >
                    <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                      <Plus className="size-4" />
                    </div>
                    <div className="text-muted-foreground font-normal">
                      Add workspace
                    </div>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      {isOpenGroupModal && (
        <GroupModal
          isOpen={isOpenGroupModal}
          onOpenChange={(value) => setIsOpenGroupModal(value)}
        />
      )}
    </>
  );
};
